import {KurvLight} from './../../../models/kurvLightModel';
import {Kurv} from './../../../models/Kurv';
import {OrdningsVerdi} from './../../../models/ordningsVerdi';
import {kurverServices} from './../../services/index';
import {MutationDefinition, QueryDefinition, QueryExtraOptions} from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import {MenyValg, PaginatedResult} from '../../../models';
import {FetchArg, PageParam, SearchQueries, TBaseQueryFn, TEntityDefinitions} from '../queryApiCreator';
import {provideTagList, QueryApiService} from '../queryApiService';
import {getNextPageURL} from '../../services/requestService';

interface KurvDefinitions
  extends TEntityDefinitions<Kurv, typeof kurverServices.typeName> {
  treeviewListKurv: QueryDefinition<{ id: string; searchString: string; expanded: boolean },TBaseQueryFn<Kurv>,any,PaginatedResult<Kurv>,any >;
  getItemChildrenKurv: QueryDefinition<{ query: string; expanded: boolean }, TBaseQueryFn<Kurv>,any,PaginatedResult<Kurv>,string >;
  treeviewSearchKurv: QueryDefinition<string | Kurv | Partial<Kurv>,TBaseQueryFn<Kurv>,any,PaginatedResult<Kurv>,string>;
  searchKurvLight: QueryDefinition<{val : KurvLight , param? : PageParam},TBaseQueryFn<KurvLight>,any,PaginatedResult<KurvLight>,string>;
  hentDistribusjon: QueryDefinition<FetchArg<MenyValg>, TBaseQueryFn<MenyValg>, '', PaginatedResult<MenyValg>, string> & QueryExtraOptions<'' | 'id', PaginatedResult<MenyValg>, FetchArg<MenyValg>, TBaseQueryFn<MenyValg>, string>,
  updateDistribusjon: MutationDefinition<MenyValg[], TBaseQueryFn<any>, any, void, any>,
  deleteDistribusjon: MutationDefinition<MenyValg, TBaseQueryFn<MenyValg>,'', void, any>,
  deleteListeDistribusjon: MutationDefinition<number[], TBaseQueryFn<number[]>,'', void, any>,
  hentDistribusjonTilKurv: QueryDefinition<FetchArg<MenyValg> & {kurvId: string;}, TBaseQueryFn<any>, any, PaginatedResult<MenyValg>, string>,
  createDistribusjon: MutationDefinition<{kurvId: string, items: MenyValg[]}, TBaseQueryFn<any>, any, void, 'api'>
}

class KurvQueryApiService extends QueryApiService<
  Kurv,
  typeof kurverServices.typeName,
  KurvDefinitions
> {
  constructor() {
    super(kurverServices);
    this.api.enhanceEndpoints({
      addTagTypes: ['distribusjonList'],
    });
    this.api.injectEndpoints({
      endpoints: build => {
        return {
          getItemChildrenKurv: build.query< PaginatedResult<Kurv>, { query: string; expanded: boolean } >({
            query: (arg: { query: string; expanded: boolean }) => ({
              request: kurverServices.kurverTreeSok({ id: arg.query } as Kurv, arg.expanded),
            }),
            providesTags: (result) => provideTagList(result?.value, kurverServices.typeName)
          }),
          treeviewSearchKurv: build.query<PaginatedResult<Kurv>, string | Kurv | Partial<Kurv>>({
            query: (query: string | OrdningsVerdi) => {
              if (typeof query === 'object') {
                return {
                  request: kurverServices.kurverTreeSok(query),
                  meta: query,
                };
              }
              return {
                request: kurverServices.plainSearch(query as string),
                meta: query,
              };
            },
            providesTags: (result) => provideTagList(result?.value, kurverServices.typeName)
          }),
          searchKurvLight: build.query<PaginatedResult<KurvLight>, {val : KurvLight , param? : PageParam}>({
            query: (arg: {val : KurvLight , param? : PageParam}) => ({ request: kurverServices.searchKurvLight(arg.val,{skip: arg?.param?.skip ?? 10, top: arg?.param?.top ?? 10 }),}),
            providesTags: (result) => provideTagList(result?.value, kurverServices.typeName)
          }),
          treeviewListKurv: build.query<PaginatedResult<Kurv>, { id: string; searchString: string; expanded: boolean }>({
            query: (arg: {id: string; searchString: string; expanded: boolean;  }) => ({
              request: kurverServices.kurverTreeList(arg.searchString, arg.id,arg.expanded),
            }),
            providesTags: (result) => provideTagList(result?.value, kurverServices.typeName)
          }),
          hentDistribusjon: build.query<PaginatedResult<MenyValg>, FetchArg<MenyValg>>({
            query: ({filter, param, query}: FetchArg<MenyValg>) => {          
              if (query) {         
                if (typeof query === 'object') {
                  return ({ request: kurverServices.sokDistribusjon({ value: query, filter } as SearchQueries<MenyValg>,param), meta: query });
                }
              }
              return ({ request: kurverServices.hentDistribusjon(param ? getNextPageURL({endpoint: `${this.serviceName}/hentDistribusjon`, param}) : undefined) });
            },
            providesTags: (result) => ['distribusjonList'],
          }),
          hentDistribusjonTilKurv: build.query<PaginatedResult<MenyValg>, FetchArg<MenyValg> & { kurvId: string }>({
            query: arg => {
                return ({ request: kurverServices.hentDistribusjonTilKurv(arg.kurvId) });
            },
            onQueryStarted(arg, api) {
                
            },
            providesTags: (res) => [{type: kurverServices.serviceName, id: 'LIST'}, 'distribusjonList']
        }),
          deleteDistribusjon: build.mutation<void, MenyValg>({
            query: arg => ({ request: kurverServices.deleteDistribusjon(arg.id) }),
            invalidatesTags: (resp, err, arg) => ['distribusjonList'],
          }),
          deleteListeDistribusjon: build.mutation<void, number[]>({
            query: arg => ({ request: kurverServices.deleteListeDistribusjon(arg) }),
            invalidatesTags: (resp, err, arg) => ['distribusjonList'],
          }),
          updateDistribusjon: build.mutation<void, MenyValg[]>({
            query: arg => ({ request: kurverServices.updateDistribusjon(arg) }),
            invalidatesTags: (resp, err, arg) => ['distribusjonList'],
          }),
          createDistribusjon: build.mutation<void, {kurvId: string, items: MenyValg[]}>({
            query: ({kurvId, items}) => ({ request: kurverServices.createDistribusjon(kurvId, items) }),
            invalidatesTags: (resp, err, arg) => ['distribusjonList'],
          }),
        };
      },
    });
  }
}

export const kurvQueryApiService = new KurvQueryApiService();
